import * as Sentry from '@sentry/vue'
import GenericUploader from './GenericUploader'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import utils from '@/util'
import i18nPresenter from '@/i18nPresenter'
import UploadFeatureName from '@/constant/uploadFeatureName'
import OpenAIConstants from '@/constant/openAI'

// api
import UploadApi from '@/api/upload'

// tracking
import { IMPORT_DOCUMENT } from '@/constant/eventActions'
import eventCategories from '@/constant/eventCategories'
import SlideMixpanelTracker from '@/utils/mixpanel/slide.mixpanel-tracker'
import { UPLOAD_FILE_STARTED } from '@/constant/eventActions/ImportEventActions.js'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

import store from '@/vuex/store'
// Explanation:
// We import the Vuex store directly to ensure the latest state, because Vuex state passed as parameters
// is not reactive in plain JavaScript classes. This violates the Dependency Inversion Principle (DIP)
// of SOLID, creating tight coupling and making the class less modular and harder to test in isolation.

// constants
import importStatusConstant from '@/constant/importStatus'

const MAX_FILE_SIZE = 52428800
const AI_IMPORT_FILE_TYPE = ['pptx', 'ppt', 'pdf']
const CONTENT_TYPE_HEADER = 'Content-Type'
const FORM_URL_ENCODED = 'application/x-www-form-urlencoded'

// actions
const SET_SHOW_UPLOAD_AUDIO_MODAL_ACTION = 'audioUploader/setShowUploadAudioModalAction'
const SET_IS_UPLOADING_AUDIO_DONE_ACTION = 'audioUploader/setIsUploadingAudioDoneAction'
const SET_UPLOAD_AUDIO_ERROR_ACTION = 'audioUploader/setUploadAudioErrorAction'
const SET_IS_UPLOADING_AUDIO_ACTION = 'audioUploader/setIsUploadingAudioAction'

const SET_SPREADSHEET_IMPORT_ERROR_ACTION = 'spreadsheetUploader/setSpreadsheetImportErrorAction'
const SET_SPREADSHEET_IMPORT_STATUS_ACTION = 'spreadsheetUploader/setSpreadSheetImportStatusAction'
const SET_DOC_TO_SLIDE_RESPONSE_ERROR_ACTION = 'docToSlideGenerator/setDocToSlideResponseErrorAction'
const SET_GENERATE_FROM_DOC_IMPORT_STATUS_ACTION = 'docToSlideGenerator/setDocToSlideImportStageAction'
const RESET_DOC_TO_SLIDE_GENERATION_DONE_ACTION = 'docToSlideGenerator/resetIsDocToSlideGenerationDoneAction'
const HANDLE_GENERATE_FROM_DOC_ERROR_ACTION = 'docToSlideGenerator/handleDocToSlideErrorAction'

const SET_AI_CHATBOT_IMPORT_ERROR_ACTION = 'aiChatbotImportPptxPdf/setAIChatbotImportSlidesErrorAction'

const CREATE_NEW_CONVERSATION_MESSAGE_ACTION = 'aiConversation/createNewMessageAction'
class BootstrapUploader extends GenericUploader {
  constructor(
    // local variables - GENERATE FROM DOC
    needGenerateQuiz,

    // local variables - IMPORT PPT PDF
    customPropsForTracking,

    // local variables - IMPORT SPREADSHEET
    routeName,

    // local variables - UPLOAD IMAGE
    signedCFUrlExpiryDate,
    featureToUploadImage,
  ) {
    super()
    // local variables - GENERATE FROM DOC
    this.needGenerateQuiz = needGenerateQuiz

    // local variables - IMPORT PPT PDF
    this.gaCategory = customPropsForTracking.gaCategory
    this.eventLabel = customPropsForTracking.eventLabel
    this.eventValue = customPropsForTracking.eventValue

    // local variables - IMPORT SPREADSHEET
    this.routeName = routeName

    // local variables - UPLOAD IMAGE
    this.signedCFUrlExpiryDate = signedCFUrlExpiryDate
    this.featureToUploadImage = featureToUploadImage

    // local variables - BOOTSTRAP UPLOADER
    this.presentationId = store.state.presentation.id
    this.storeGetters = store.getters
  }

  upload(files, featureName) {
    if (!files || !files.length || !featureName) return
    if (featureName === UploadFeatureName.AI_IMPORT_PPT_PDF) {
      this.uploadPptPdfForAI(files)
      return
    }
    if (featureName === UploadFeatureName.IMPORT_PPT_PDF) {
      this.uploadPptPdf(files)
      return
    }
    if (featureName === UploadFeatureName.IMPORT_SPREADSHEET) {
      this.uploadSpreadsheet(files)
      return
    }
    if (featureName === UploadFeatureName.UPLOAD_AUDIO) {
      this.uploadAudio(files)
      return
    }
    if (featureName === UploadFeatureName.UPLOAD_IMAGE) {
      this.uploadImage(files)
      return
    }
    if (featureName === UploadFeatureName.AI_CHATBOT_IMPORT || featureName === UploadFeatureName.UPLOAD_V2) {
      this.uploadFileV2(files)
    }
  }

  // RE-USE FUNCTIONS -----------------------------------------------------------------------------------------------
  async _addMessageToAIPanel(fileName) {
    try {
      store.dispatch(CREATE_NEW_CONVERSATION_MESSAGE_ACTION, {
        content: fileName,
        presentationId: this.presentationId,
        role: OpenAIConstants.AI_ROLE_USER,
        type: 'generate-from-doc',
      }, { root: true })
      await utils.sleep(500)
      store.dispatch(CREATE_NEW_CONVERSATION_MESSAGE_ACTION, {
        content: i18nPresenter.t('Your file is being uploaded...'),
        presentationId: this.presentationId,
        role: OpenAIConstants.AI_ROLE_SYSTEM,
        type: OpenAIConstants.AI_SYSTEM_PENDING_MESSAGE_TYPE,
      }, { root: true })
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  // GENERATE FROM DOC -----------------------------------------------------------------------------------------------
  _setFormDataUploadPptPdfForAI(file) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('socketId', Socket.getSocketId())
    formData.append('uniqueAccessCode', store.state.presentation.uniqueAccessCode)
    formData.append('fileSize', file.size)
    formData.append('fileName', file.name)
    if (this.needGenerateQuiz && !store.state.docToSlideGenerator.isDocToSlideGenerating) {
      formData.append('needGenerateQuiz', this.needGenerateQuiz)
      const requestId = utils.generateRandomString()
      store.dispatch('docToSlideGenerator/generateDocToSlideUniqueRequestIdAction', `${requestId}-${store.state.presentation.id}`, { root: true })
      formData.append('requestId', store.state.docToSlideGenerator.docToSlideUniqueRequestId)
      EventBus.$emit('doc-to-quiz: reset_all_results')
    }
    if (store.state.presentation.id) {
      formData.append('presentationId', store.state.presentation.id)
    }
    const headers = new Headers()
    headers.append(CONTENT_TYPE_HEADER, FORM_URL_ENCODED)
    return formData
  }

  async uploadPptPdfForAI(files) {
    try {
      const file = files[0]
      await store.dispatch('rightNav/openAIPanelAction', null, { root: true })
      store.dispatch('aiConversation/resetNumberOfSlidesAddedAction', null, { root: true })
      store.dispatch('docToSlideGenerator/resetIsDocToSlideGenerationDoneAction', null, { root: true })
      store.dispatch(SET_DOC_TO_SLIDE_RESPONSE_ERROR_ACTION, null, { root: true })
      await this._addMessageToAIPanel(file.name)
      if (file && file.size > MAX_FILE_SIZE) {
        store.dispatch(SET_DOC_TO_SLIDE_RESPONSE_ERROR_ACTION,
          importStatusConstant.ERROR_REACH_LIMIT,
          { root: true })
        store.dispatch(HANDLE_GENERATE_FROM_DOC_ERROR_ACTION, null, { root: true })
        return
      }

      const ext = file.name.split('.').pop()
      if (ext.indexOf('ppt') === -1 && ext.indexOf('pdf') === -1) {
        store.dispatch(SET_DOC_TO_SLIDE_RESPONSE_ERROR_ACTION,
          importStatusConstant.ERROR_UNSUPPORTED_FILE_TYPE,
          { root: true })
        store.dispatch(HANDLE_GENERATE_FROM_DOC_ERROR_ACTION, null, { root: true })
        return
      }

      store.dispatch(SET_GENERATE_FROM_DOC_IMPORT_STATUS_ACTION, importStatusConstant.UPLOADING, { root: true })
      const formData = this._setFormDataUploadPptPdfForAI(file)
      await UploadApi.uploadDocToQuiz(formData)
      store.dispatch(SET_GENERATE_FROM_DOC_IMPORT_STATUS_ACTION, importStatusConstant.CONVERTING, { root: true })
    } catch (error) {
      store.dispatch('docToSlideGenerator/toggleDocToSlideImportModalAction', false, { root: true })
      store.dispatch(SET_GENERATE_FROM_DOC_IMPORT_STATUS_ACTION, importStatusConstant.NO_IMPORT, { root: true })
      store.dispatch(HANDLE_GENERATE_FROM_DOC_ERROR_ACTION, null, { root: true })
    }
  }

  // IMPORT PPT PDF -----------------------------------------------------------------------------------------------
  async _setFormDataImport(file) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('uniqueAccessCode', store.state.presentation.uniqueAccessCode)
    formData.append('socketId', Socket.getSocketId())
    formData.append('type', 'importPpt')
    formData.append('fileSize', file.size)
    formData.append('fileName', file.name)
    if (this.needGenerateQuiz && !store.state.docToSlideGenerator.isDocToSlideGenerating) {
      formData.append('needGenerateQuiz', this.needGenerateQuiz)
      const requestId = utils.generateRandomString()

      store.dispatch('docToSlideGenerator/generateDocToSlideUniqueRequestIdAction',
        `${requestId}-${store.state.presentation.id || null}`, { root: true })
      formData.append('requestId', store.state.docToSlideGenerator.docToSlideUniqueRequestId)
      store.dispatch(SET_GENERATE_FROM_DOC_IMPORT_STATUS_ACTION, importStatusConstant.UPLOADING, { root: true })
      EventBus.$emit('doc-to-quiz: reset_all_results')
      await store.dispatch('rightNav/openAIPanelAction', null, { root: true })
      await store.dispatch('aiConversation/setGreetingsMessageAction', null, { root: true })
      store.dispatch('aiConversation/setAddMessageSourceAction', OpenAIConstants.AI_DOC_TO_SLIDE_ADD_MESSAGE_FROM, { root: true })
      await this._addMessageToAIPanel(file.name)
    }
    if (store.state.selections.selectedFolderId) {
      formData.append('folderId', store.state.selections.selectedFolderId)
    }
    if (store.state.presentation.id) {
      formData.append('presentationId', store.state.presentation.id)
    }
    return formData
  }

  _trackBeginUploadFile() {
    const eventAction = `${IMPORT_DOCUMENT} > Begin uploading`
    const trackData = {
      eventAction,
      eventCategory: eventCategories.PRESENTATION_EDITOR,
      isGenerateQuizFromThisDocument: this.needGenerateQuiz,
      presentationId: store.state.presentation.id,
      userId: store.state.currentUser.id,
    }
    utils.trackGA4AndMixpanel(eventAction, trackData)
  }

  async uploadPptPdf(files) {
    if (!files || !files.length) return
    const file = files[0]
    store.state.importError = ''
    if (file && file.size > MAX_FILE_SIZE) {
      store.state.importError =
        '<div>Maximum file size is 50 MB. Maximum slide amount is 100 slides. <br /> Please try again with a smaller file.</div>'
      utils.mixpanelTracking(this.gaCategory, 'Import > Upload file', this.eventLabel, `${this.eventValue}${this.eventValue ? ' > ' : ''}'Exceed 50MB'`)
      return
    }

    const ext = file.name.split('.').pop()
    if (ext.indexOf('ppt') === -1 && ext.indexOf('pdf') === -1) {
      store.state.importError =
        'Could not upload this file (only support file pptx, ppt, pdf).'
      utils.mixpanelTracking(this.gaCategory, 'Import > Upload file', this.eventLabel, `${this.eventValue}${this.eventValue ? ' > ' : ''}'Invalid file type'`)
      return
    }

    store.state.importStatus = 2
    this._trackBeginUploadFile()
    utils.mixpanelTracking(this.gaCategory, 'Import > Processing', this.eventLabel, `File size: ${file.size}`)
    const formData = await this._setFormDataImport(file)
    const headers = new Headers()
    headers.append(CONTENT_TYPE_HEADER, FORM_URL_ENCODED)
    // Upload presentation logo
    await UploadApi.uploadPptPdf(formData)
    store.state.importStatus = 3
    if (this.needGenerateQuiz) {
      store.dispatch(RESET_DOC_TO_SLIDE_GENERATION_DONE_ACTION, { root: true })
      store.dispatch(SET_GENERATE_FROM_DOC_IMPORT_STATUS_ACTION, importStatusConstant.CONVERTING, { root: true })
    }
  }

  // IMPORT SPREADSHEET -----------------------------------------------------------------------------------------------
  trackUploadFile() {
    const slideTracker = new SlideMixpanelTracker(store.state.presentation, this.routeName)
    slideTracker.trackCreateNewSlide('Import Spreadsheet', null)
  }

  async uploadSpreadsheet(files) {
    utils.mixpanelTracking(
      eventCategories.PRESENTATION_EDITOR,
      'Import Spreadsheet > Browse file',
    )
    const file = files[0]
    store.dispatch(SET_SPREADSHEET_IMPORT_ERROR_ACTION, '')
    if (file && file.size > 1024000) {
      store.dispatch(SET_SPREADSHEET_IMPORT_ERROR_ACTION, i18nPresenter.t('maximumFileSize1M'))
      utils.mixpanelTracking(
        'Presentation',
        'Import .xlsx',
        'Exceed 1MB',
      )
      return
    }

    const ext = file.name.split('.').pop()
    if (ext.indexOf('xlsx') === -1) {
      store.dispatch(SET_SPREADSHEET_IMPORT_ERROR_ACTION, i18nPresenter.t('Could not upload this file (only support file xlsx).'))
      utils.mixpanelTracking(
        'Presentation',
        'Import xlsx',
        'Invalid file type',
      )
      return
    }
    store.dispatch(SET_SPREADSHEET_IMPORT_STATUS_ACTION, 2)
    const formData = new FormData()
    formData.append('file', file)
    formData.append('socketId', Socket.getSocketId())
    formData.append('fileSize', file.size)
    formData.append('fileName', file.name)
    formData.append('id', store.state.presentation.id)
    const headers = new Headers()
    headers.append(CONTENT_TYPE_HEADER, FORM_URL_ENCODED)

    const resp = await UploadApi.uploadXlsx(formData)
    store.dispatch(SET_SPREADSHEET_IMPORT_STATUS_ACTION, 2)
    try {
      const respSlides = await UploadApi.importSpreadsheet({
        presentationId: store.state.presentation.id,
        file: resp.body.file.key,
      })
      if (respSlides.body.length) {
        store.state.isNewPresentation = false
        if (!store.state.presentation.slides[store.state.presentation.slides.length - 1].id) {
          store.state.presentation.slides.splice(store.state.presentation.slides.length - 1, 1)
        }
        store.state.presentation.slides = store.state.presentation.slides.concat(respSlides.body)
        setTimeout(() => {
          EventBus.$emit('makeSlideActive', respSlides.body[0])
          EventBus.$emit('ImportSheet: handleSuggestPresentationTitle', respSlides.body[0])
        }, 500)
      }
      store.dispatch(SET_SPREADSHEET_IMPORT_STATUS_ACTION, 3)
      store.dispatch('setImportSheetModalStateAction', false)
      utils.showToastSuccess(i18nPresenter.t('Import Done!'))
      this.trackUploadFile()
    } catch {
      store.dispatch(SET_SPREADSHEET_IMPORT_ERROR_ACTION, i18nPresenter.t('Please recheck your file or contact support'))
    } finally {
      store.dispatch(SET_SPREADSHEET_IMPORT_STATUS_ACTION, 1)
    }
  }

  // UPLOAD AUDIO -----------------------------------------------------------------------------------------------
  /* eslint-disable class-methods-use-this */
  async uploadAudio(files) {
    const file = files[0]
    store.dispatch(SET_UPLOAD_AUDIO_ERROR_ACTION, '')
    store.dispatch(SET_IS_UPLOADING_AUDIO_ACTION, true)
    if (file && file.size > 15728640) {
      store.dispatch(SET_UPLOAD_AUDIO_ERROR_ACTION, i18nPresenter.t('Could not upload this file (max size is 15 MB).'))
      store.dispatch(SET_IS_UPLOADING_AUDIO_ACTION, false)
      return
    }
    if (file.type.indexOf('audio') == -1) {
      store.dispatch(SET_UPLOAD_AUDIO_ERROR_ACTION, i18nPresenter.t('Could not upload this file (only support audio file mp3).'))
      store.dispatch(SET_IS_UPLOADING_AUDIO_ACTION, false)
      return
    }

    const ext = file.name.split('.').pop()
    if (ext === 'mp3') {
      const formData = new FormData()
      formData.append('file', file)
      formData.append(
        'accessCode',
        store.state.presentation.uniqueAccessCode,
      )
      formData.append('socketId', Socket.getSocketId())
      formData.append('presentationId', store.state.presentation.id)
      formData.append('slideId', store.state.slideActive.id)
      const headers = new Headers()
      headers.append(CONTENT_TYPE_HEADER, FORM_URL_ENCODED)

      try {
        const res = await UploadApi.uploadAudio(store.state.presentation.id, formData)
        store.dispatch(SET_SHOW_UPLOAD_AUDIO_MODAL_ACTION, false)
        store.dispatch(SET_IS_UPLOADING_AUDIO_ACTION, false)
        store.dispatch(SET_IS_UPLOADING_AUDIO_DONE_ACTION, true)

        const payload = {
          audioLink: res.body.file.link,
          audioName: res.body.file.name,
        }
        EventBus.$emit('Upload Audio: updateSlides', payload)
      } catch (err) {
        store.dispatch(SET_UPLOAD_AUDIO_ERROR_ACTION, i18nPresenter.t('Something went wrong'))
        store.dispatch(SET_IS_UPLOADING_AUDIO_ACTION, false)
      }
    } else {
      store.dispatch(SET_UPLOAD_AUDIO_ERROR_ACTION, i18nPresenter.t('Could not upload this file (only support audio file mp3).'))
      store.dispatch(SET_IS_UPLOADING_AUDIO_ACTION, false)
    }

    utils.mixpanelTracking(
      eventCategories.PRESENTATION_EDITOR,
      store.getters.selectedSlides.length > 1
        ? 'Audio Tab > Upload audio for multiple slides'
        : 'Audio Tab > Upload audio for single slide',
      `Presentation: ${store.state.presentation.id}`,
    )
  }

  // UPLOAD IMAGE
  async uploadImage(files) {
    EventBus.$emit('Upload Image V2: trackUploadImage', 'manual_upload')
    const file = files[0]

    if (file && file.size > 15728640) {
      store.state.uploadLocalImageError = i18nPresenter.t('Could not upload this file (max size is 15 MB).')
      return
    }

    const errorString = i18nPresenter.t('Could not upload this file (only support image file jpg, svg, gif, png, ...).')
    if (file.type && !file.type.includes('image')) {
      store.state.uploadLocalImageError = errorString
      return
    }

    store.state.uploadLocalImageError = ''
    store.state.isUploadingLocalImage = true
    const formData = new FormData()

    formData.append('file', file)
    if (this.signedCFUrlExpiryDate) {
      formData.append('expires', this.signedCFUrlExpiryDate)
    }
    formData.append('accessCode', store.state.presentation.uniqueAccessCode)
    formData.append('socketId', Socket.getSocketId())
    formData.append('type', this.featureToUploadImage)

    if (file.type === 'image/gif') {
      // need to infer the gif image dimension to be insert as a block
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (e) => {
        const image = new Image()
        image.src = e.target.result
        image.onload = () => {
          file.width = image.width
          file.height = image.height
        }
      }
    }

    try {
      const res = await UploadApi.uploadImageV2(formData)
      if (file.type === 'image/gif') {
        const payload = {
          imageUrl: res.body.file.location,
          selectedSlides: store.getters.selectedSlides,
          imageDimension: {
            width: file.width,
            height: file.height,
          },
        }
        EventBus.$emit('Upload Image V2: updateGifInBackground function', payload)
      } else {
        EventBus.$emit('Upload Image V2: callbackUploadImageV2', res.body.file.location, 'manual_upload', file)
      }
    } catch (error) {
      if (error.body && error.body.code === 'file_not_supported') {
        store.state.uploadLocalImageError = errorString
      } else {
        store.state.uploadLocalImageError = 'Cannot upload file. Please try again.'
      }

      store.state.isUploadingLocalImage = false
      throw error
    }
  }

  // AI CHATBOT IMPORT -----------------------------------------------------------------------------------------------
  trackBeginUploadFileV2(file) {
    const eventAction = UPLOAD_FILE_STARTED
    const trackData = {
      eventAction,
      eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.routeName],
      file_name: file?.name,
    }
    utils.trackGA4AndMixpanel(eventAction, trackData)
  }

  uploadFileV2(files) {
    try {
      if (!(this.storeGetters['aiChatbotImportPptxPdf/canUseAIChatbotImportPptxPdf'] || this.storeGetters['aiChatbotImportPptxPdf/canUseAIChatbotImportUIUX'])) return
      if (!files || !files.length) return

      const file = files[0]
      store.dispatch(SET_AI_CHATBOT_IMPORT_ERROR_ACTION, '')
      if (file && file.size > MAX_FILE_SIZE) {
        store.dispatch(SET_AI_CHATBOT_IMPORT_ERROR_ACTION, i18nPresenter.t('uploadFileLimitationsError'))
        return
      }

      const ext = file.name.split('.').pop()
      if (!AI_IMPORT_FILE_TYPE.includes(ext)) {
        store.dispatch(SET_AI_CHATBOT_IMPORT_ERROR_ACTION,
          i18nPresenter.t(importStatusConstant.ERROR_UNSUPPORTED_FILE_TYPE))
        return
      }

      this.trackBeginUploadFileV2(file)
      store.dispatch('aiChatbotImportPptxPdf/uploadFileAction', file, { root: true })
    } catch (error) {
      store.dispatch(SET_AI_CHATBOT_IMPORT_ERROR_ACTION, i18nPresenter.t('Could not upload this file. Please try again.'))
      Sentry.captureException(error)
    }
  }
}

export default BootstrapUploader
